/**
 * L2RefinementSection Component
 *
 * Progressive search refinement: Shows L2 category pills after L1 selection.
 * Pills are greyed out until selected, then become highlighted.
 *
 * MVP 1.3 Phase 2: Progressive Search Pattern
 *
 * Location: apps/web/src/components/search/L2RefinementSection.jsx
 * Created: 2025-11-26
 */

import React, { useMemo } from 'react';
import useTaxonomy from '../../hooks/useTaxonomy';

/**
 * L2RefinementSection Component
 * @param {string|null} selectedL1Slugs - Comma-separated L1 slugs (from URL)
 * @param {string|null} selectedL2Slugs - Comma-separated L2 slugs (from URL)
 * @param {Function} onL2Toggle - Callback when L2 is toggled: onL2Toggle(slug)
 */
export default function L2RefinementSection({
  selectedL1Slugs = null,
  selectedL2Slugs = null,
  onL2Toggle = () => {}
}) {
  const { taxonomy, loading: taxonomyLoading } = useTaxonomy();

  /**
   * Get all L2 categories under the selected L1 categories
   */
  const availableL2Categories = useMemo(() => {
    if (taxonomyLoading || !taxonomy || !selectedL1Slugs) {
      return [];
    }

    const l1Slugs = selectedL1Slugs.split(',').map(s => s.trim());
    const l2Selected = selectedL2Slugs ? selectedL2Slugs.split(',').map(s => s.trim()) : [];
    const l2Categories = [];

    // Find all L2 categories under the selected L1s
    if (taxonomy.categories && Array.isArray(taxonomy.categories)) {
      taxonomy.categories.forEach(l1 => {
        if (l1Slugs.includes(l1.slug)) {
          if (l1.children && Array.isArray(l1.children)) {
            l1.children.forEach(l2 => {
              l2Categories.push({
                slug: l2.slug,
                name: l2.name,
                parentName: l1.name,
                parentSlug: l1.slug,
                isSelected: l2Selected.includes(l2.slug)
              });
            });
          }
        }
      });
    }

    return l2Categories;
  }, [taxonomy, taxonomyLoading, selectedL1Slugs, selectedL2Slugs]);

  /**
   * Group L2 categories by parent L1
   */
  const groupedByParent = useMemo(() => {
    const groups = {};
    availableL2Categories.forEach(l2 => {
      if (!groups[l2.parentName]) {
        groups[l2.parentName] = [];
      }
      groups[l2.parentName].push(l2);
    });
    return groups;
  }, [availableL2Categories]);

  // Don't render if no L1 is selected (after all hooks are called)
  if (!selectedL1Slugs || availableL2Categories.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-gray-700 mb-2">
        Specific Adventures
      </div>

      {Object.entries(groupedByParent).map(([parentName, l2Categories]) => (
        <div key={parentName} className="space-y-2">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
            {parentName}
          </div>

          <div className="flex flex-wrap gap-2">
            {l2Categories.map((l2) => (
              <button
                key={l2.slug}
                onClick={() => onL2Toggle(l2.slug)}
                className={`
                  inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition
                  ${l2.isSelected
                    ? 'bg-purple-100 border-2 border-purple-500 text-purple-900'
                    : 'bg-gray-100 border border-gray-300 text-gray-600 hover:bg-gray-200'
                  }
                `}
              >
                {l2.name}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
